import type { TrackingFn } from '@change-corgi/core-react/tracking';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { InferPetitionScopeLocation } from './api.graphql';
import type { InferPetitionScopeLocationMutation, InferPetitionScopeLocationMutationVariables } from './api.graphql';

type Input = { mainPrompt: string; personalStory?: string };

/**
 * The parsed success payload. `city`/`adminArea`/`countryCode` were added for CHANGE-98274 (to
 * build the DMI mutation's `relevantLocation` — `countryCode` is a real ISO code per the inference
 * prompt, same as `adminArea`); `name` predates that and backs the existing `inferred_geo_location`
 * tracking property. The selection still omits the model's free-text `reasoning`: this is a
 * background analytics signal and that content is unbounded, so it must not flow into tracking.
 */
type InferScopeLocationResult = Extract<
	InferPetitionScopeLocationMutation['inferPetitionScopeLocation'],
	{ __typename: 'InferPetitionScopeLocationSuccess' }
>;

/**
 * Thin wrapper over the middle-end `inferPetitionScopeLocation` mutation, tracking its own success and
 * error the same way `generateAiPetitionDraft`'s `api.ts` does. Returns `null` when there's no prompt
 * to infer from (no call, no tracking). Throws on the typed-error arm and on transport failure so the
 * caller can discriminate success vs. failure; callers that treat this as best-effort (the SAP
 * background call) are responsible for swallowing the rejection.
 */
export async function inferPetitionScopeLocation(
	input: Input,
	{ gql: { fetch } }: UtilityContext,
	track: TrackingFn,
): Promise<InferScopeLocationResult | null> {
	if (!input.mainPrompt) return null;

	let response: InferPetitionScopeLocationMutation['inferPetitionScopeLocation'];
	try {
		const { data } = await fetch<InferPetitionScopeLocationMutation, InferPetitionScopeLocationMutationVariables>({
			query: InferPetitionScopeLocation,
			// Matches the mutation's @restrictPath directive; required for the call to resolve.
			path: '/infer-petition-scope-location',
			variables: { input },
			rejectOnError: true,
		});
		response = data.inferPetitionScopeLocation;
	} catch (e: unknown) {
		void track('sap_scope_inference_error', { scope_inference_error_reason: 'TRANSPORT_ERROR' });
		throw e;
	}

	// `reason` is the generated error-reason enum, tracked directly here (rather than round-tripped
	// through a thrown Error's message) so a new server value is a type error against
	// `scope_inference_error_reason`, not a silent collapse to TRANSPORT_ERROR.
	if (response.__typename !== 'InferPetitionScopeLocationSuccess') {
		void track('sap_scope_inference_error', { scope_inference_error_reason: response.reason });
		throw new Error(response.reason);
	}

	void track('sap_scope_inference_completed', {
		inferred_scope: response.scope,
		inferred_admin_level: response.adminLevel,
		scope_confidence: response.scopeConfidence,
		inferred_target_kind: response.targetKind,
		names_place_in_text: response.namesPlaceInText,
		inferred_location_source: response.location.source,
		inferred_location_resolved: response.location.resolved,
		inferred_location_confidence: response.location.confidence,
	});

	return response;
}
